import { Link } from "wouter";
import { CarLogo } from "../common/CarLogo.js";
import { useT } from "../../i18n/index.js";
import { formatMoney } from "../../lib/format-fr.js";
import { abrirArquivo } from "../../lib/open-file.js";
import { trpc } from "../../lib/trpc.js";
import { VEHICLE_STATUS_STYLE } from "../../lib/vehicle-status.js";

/** dd/mm/aaaa a partir de "aaaa-mm-dd". */
function dataFr(iso: string | null): string {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return d && m && y ? `${d}/${m}/${y}` : iso;
}

/**
 * CEO 2026-10-26 (GK-294-GV) — FICHA DO VEÍCULO, como no eDSP: o veículo e
 * todos os dossiers dele, do mais novo ao mais antigo. Cada dossier é um
 * serviço (devis → reparo → fatura → recebido); o carro que volta ganha um
 * dossier novo. Abre pela placa na ficha do dossier, na lista de carros, nas
 * faturas e no cadastro de veículos.
 */
export function VehicleHistoryModal({
  companyId,
  vehicleId,
  currentDossierId,
  onClose,
}: {
  companyId: number;
  vehicleId: number;
  /** Dossier de onde a ficha foi aberta (marcado na lista). */
  currentDossierId?: number;
  onClose: () => void;
}) {
  const t = useT();
  const q = trpc.vehicles.history.useQuery({ companyId, vehicleId });
  const utils = trpc.useUtils();
  const v = q.data?.vehicle;
  const dossiers = q.data?.dossiers ?? [];
  const marcaModelo = [v?.make, v?.model].filter(Boolean).join(" ");

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 sm:items-center"
      onClick={onClose}
      data-testid="vehicle-history-modal"
    >
      <div
        className="w-full max-w-2xl rounded-lg border border-neutral-800 bg-neutral-900 p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {q.isLoading && <p className="text-sm text-neutral-500">{t("vehicles.loading")}</p>}
        {q.error && <p className="text-sm text-red-400">{q.error.message}</p>}
        {v && (
          <>
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <CarLogo make={v.make} size={40} />
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-wider text-neutral-500">
                    {t("vehicles.historyTitle")}
                  </p>
                  <p className="whitespace-nowrap font-mono text-xl font-bold tracking-wide text-neutral-100">
                    {v.plate ?? v.vin ?? "—"}
                  </p>
                  {marcaModelo && (
                    <p className="truncate text-sm text-neutral-300">{marcaModelo}</p>
                  )}
                  {v.plate && v.vin && (
                    <p className="truncate font-mono text-[11px] text-neutral-500">VIN {v.vin}</p>
                  )}
                  {v.customerName && (
                    <p className="truncate text-xs uppercase text-neutral-500">{v.customerName}</p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 rounded-md border border-neutral-700 px-2 py-1 text-xs text-neutral-400 hover:text-neutral-100"
                aria-label="✕"
              >
                ✕
              </button>
            </div>

            <p className="mt-3 text-xs text-neutral-500">
              {dossiers.length} {t("vehicles.historyDossiersWord")} · {t("vehicles.historyHint")}
            </p>

            <div className="mt-2 space-y-2">
              {dossiers.length === 0 && (
                <p className="text-sm text-neutral-500">{t("vehicles.noDossiers")}</p>
              )}
              {dossiers.map((d, i) => {
                const st = VEHICLE_STATUS_STYLE[d.status] ?? VEHICLE_STATUS_STYLE.cadastro!;
                const atual = d.id === currentDossierId;
                return (
                  <div
                    key={d.id}
                    className={`rounded-md border px-3 py-2 ${
                      atual ? "border-lime/60 bg-lime/5" : "border-neutral-800"
                    }`}
                    data-testid={`vehicle-history-dossier-${d.id}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-neutral-100">
                          {t("vehicles.historyDossierWord")} {dossiers.length - i}
                          <span className="ml-2 font-mono text-xs font-normal text-neutral-400">
                            {d.dossierCode}
                          </span>
                          {atual && (
                            <span className="ml-2 text-[11px] font-normal text-lime">
                              ← {t("vehicles.historyCurrent")}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-neutral-400">
                          {dataFr(d.devisDate ?? d.createdAt)}
                          {d.devisRef && (
                            <>
                              {" · "}
                              {t("vehicles.historyDevis")}{" "}
                              <span className="font-mono">{d.devisRef}</span>
                            </>
                          )}
                          {d.devisTotal != null && d.devisTotal > 0 && (
                            <> · {formatMoney(d.devisTotal, { decimals: 2 })} HT</>
                          )}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <span
                          style={{ background: st.bg, color: "#fff" }}
                          className="rounded px-2 py-0.5 text-[11px] font-bold"
                        >
                          {t(st.label as Parameters<typeof t>[0])}
                        </span>
                        {/* CEO 2026-10-26 — PDF original do devis (versão mais nova). */}
                        {d.devisPdfId != null && (
                          <button
                            type="button"
                            onClick={() => {
                              const attachmentId = d.devisPdfId!;
                              void abrirArquivo(
                                async () =>
                                  (
                                    await utils.attachments.getDownloadUrl.fetch({
                                      companyId,
                                      attachmentId,
                                    })
                                  ).url,
                              );
                            }}
                            data-testid={`vehicle-history-devis-pdf-${d.id}`}
                            className="rounded-md border border-neutral-700 px-2 py-0.5 text-xs text-neutral-200 hover:border-lime hover:text-lime"
                          >
                            📄 {t("vehicles.historyDevisPdf")}
                          </button>
                        )}
                        {!atual && (
                          <Link
                            href={`/dossiers/${d.id}`}
                            onClick={onClose}
                            className="rounded-md border border-neutral-700 px-2 py-0.5 text-xs text-neutral-200 hover:border-lime hover:text-lime"
                          >
                            {t("vehicles.historyOpen")}
                          </Link>
                        )}
                      </div>
                    </div>
                    {(d.technicians || d.invoices) && (
                      <p className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-neutral-500">
                        {d.technicians && <span>🔧 {d.technicians}</span>}
                        {d.invoices && <span className="font-mono">🧾 {d.invoices}</span>}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
